import 'azure-maps-control/dist/atlas.min.css';
import './PopupStyles.css';
import { AuthenticationType, ControlPosition, data as atlasData } from 'azure-maps-control';
import React, { useMemo } from 'react';
import { AzureMap,
    AzureMapDataSourceProvider,
    AzureMapLayerProvider,
    AzureMapPopup,
    AzureMapsProvider,
    IAzureMapControls,
    IAzureMapOptions } from 'react-azure-maps';
import { PolylineProps, PushpinProps } from '../../../types/MapModels';
import { AzureMapCameraControl } from './AzureMapCameraControl';
import { SYMBOL_MARKER_OFFSET_EXPRESSION } from './AzureMapMarkerConstants';
import { AzureMapSpriteRegistrar } from './AzureMapSpriteRegistrar';
import { convertPolylines, convertPushpinsToAzure, DEFAULT_CAMERA_PADDING } from './AzureMapUtils';
import { MapPopupContent } from './MapPopupContent';
import { useAzureMapCamera } from './useAzureMapCamera';

export interface AzureMapComponentProps {
    map?: IAzureMapOptions;
    polylinesProp?: PolylineProps[];
    pushpinsProp?: PushpinProps[];
    loadBubblePins?: PushpinProps[];
    alightingsBubblePins?: PushpinProps[];
    boardBubblePins?: PushpinProps[];
    infoboxesProp?: any[];
    vehiclesProp?: any[];
    onStopClick?: (args: {
        stopId?: string;
        boarding?: number;
        alighting?: number;
        load?: number;
        title?: string;
        stopName?: string;
        lat?: number;
        lon?: number;
        routeName?: string;
        routeId?: string;
    }) => void;
    showRouteSegmentPopup?: boolean;
    showZoomControls?: boolean;
    children?: React.ReactNode;
    disableAutoCamera?: boolean;
}


const AzureMapComponent: React.FC<AzureMapComponentProps> = ({
                                                                 map,
                                                                 pushpinsProp,
                                                                 polylinesProp,
                                                                 infoboxesProp,
                                                                 vehiclesProp,
                                                                 loadBubblePins,
                                                                 alightingsBubblePins,
                                                                 boardBubblePins,
                                                                 onStopClick,
                                                                 showRouteSegmentPopup = false,
                                                                 showZoomControls = false,
                                                                 children,
                                                                 disableAutoCamera = false,
                                                             }) => {

    const defaultCenter = [-95.382669, 29.757224];
    const defaultZoom = 10;
    const [lineHoverPopup, setLineHoverPopup] = React.useState<{ position: [number, number]; props: any } | null>(null);

    const convertedFeatures = useMemo(() => {
        return convertPushpinsToAzure(pushpinsProp);
    }, [pushpinsProp]);

    const loadBubble = useMemo(() => {
        return convertPushpinsToAzure(loadBubblePins);
    }, [loadBubblePins]);

    const alightingsBubble = useMemo(() => {
        return convertPushpinsToAzure(alightingsBubblePins);
    }, [alightingsBubblePins]);

    const boardingBubble = useMemo(() => {
        return convertPushpinsToAzure(boardBubblePins);
    }, [boardBubblePins]);

    const lineFeatures = useMemo(() => {
        return convertPolylines(polylinesProp);
    }, [polylinesProp]);

    const autoBounds = useAzureMapCamera({
        pushpinsProp,
        loadBubblePins,
        alightingsBubblePins,
        boardBubblePins,
        polylinesProp,
        disableAutoCamera,
    });

    const popups = useMemo(() => {
        return infoboxesProp?.map((infobox, idx) => {
            const [lat, lon] = infobox.location;
            const title = infobox.title ?? infobox.vehicleId;
            return (
                <AzureMapPopup
                    key={`popup-${idx}`}
                    isVisible={true}
                    options={{
                        position: [lon, lat],
                        closeButton: infobox.options?.showCloseButton ?? false,
                        pixelOffset: (infobox.options?.offset ?? [0, -20]) as [number, number],
                    }}
                    popupContent={
                        <MapPopupContent
                            title={title}
                            text={infobox.text}
                            minWidth={infobox.options?.minWidth}
                            padding={infobox.options?.padding}
                            accentColor={infobox.options?.popupAccentColor}
                        />
                    }
                />
            );
        });
    }, [infoboxesProp]);

    const formatValue = React.useCallback((value?: number | null) => {
        if (value === null || value === undefined) {
            return '—';
        }
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
        }
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed.toLocaleString(undefined, { maximumFractionDigits: 1 });
        }
        return '—';
    }, []);

    const handleLineHover = React.useCallback((e: any) => {
        const shape = e?.shapes?.[0];
        if (!shape) {
            setLineHoverPopup(null);
            return;
        }
        const props = shape?.getProperties ? shape.getProperties() : shape?.properties || {};
        let position: [number, number] | undefined;
        const eventPos = e?.position;
        if (Array.isArray(eventPos) && typeof eventPos[0] === 'number' && typeof eventPos[1] === 'number') {
            position = eventPos as [number, number];
        } else {
            const coords = shape?.getCoordinates ? shape.getCoordinates() : shape?.geometry?.coordinates;
            if (Array.isArray(coords) && coords.length > 0) {
                const midIndex = Math.floor(coords.length / 2);
                const mid = coords[midIndex] || coords[0];
                if (Array.isArray(mid) && typeof mid[0] === 'number' && typeof mid[1] === 'number') {
                    position = mid as [number, number];
                }
            }
        }
        if (!position) {
            setLineHoverPopup(null);
            return;
        }
        setLineHoverPopup({
            position,
            props,
        });
    }, []);

    const handleLineLeave = React.useCallback(() => {
        setLineHoverPopup(null);
    }, []);

    const hoverPopup = React.useMemo(() => {
        const props = lineHoverPopup?.props || {};
        const aggregationLabel = props.aggregation || 'Sum';
        const position = lineHoverPopup?.position || defaultCenter;
        return (
            <AzureMapPopup
                key="line-hover-popup"
                isVisible={!!lineHoverPopup}
                options={{
                    position,
                    closeButton: false,
                    pixelOffset: [0, -12],
                }}
                popupContent={<div className="popup-container" style={{ padding: '10px' }}>
                    <div className="popup-title">{props.routeLabel || 'Route segment'}</div>
                    <div className="popup-text" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span>Aggregation: {aggregationLabel}</span>
                        <span>Boardings: {formatValue(props.boarding)}</span>
                        <span>Alightings: {formatValue(props.alighting)}</span>
                        <span>Load: {formatValue(props.load)}</span>
                    </div>
                </div>}
            />
        );
    }, [lineHoverPopup, formatValue, defaultCenter]);

    const vehicleFeatures = useMemo(() => {
        return vehiclesProp?.map((p) => {
            const [lat, lon] = p.location;
            const coordinates: [number, number] = [lon, lat];
            return new atlasData.Feature(
                new atlasData.Point(coordinates),
                {
                    ...p.options,
                    ...p.eventHandlers,
                },
            );
        });
    }, [vehiclesProp]);


    React.useEffect(() => setLineHoverPopup(null), [lineFeatures]);

    const triggerStopClick = React.useCallback((shape: any) => {
        if (!onStopClick) {
            return;
        }
        const props = shape?.getProperties ? shape.getProperties() : shape?.properties || {};
        const coords = shape?.getCoordinates ? shape.getCoordinates() : shape?.geometry?.coordinates;
        const lon = Array.isArray(coords) ? coords[0] : undefined;
        const lat = Array.isArray(coords) ? coords[1] : undefined;
        onStopClick({
            stopId: props?.stopId,
            stopName: props?.stopName ?? props?.title,
            boarding: props?.boarding,
            alighting: props?.alighting,
            load: props?.load,
            title: props?.title,
            lat,
            lon,
            routeName: props?.routeName,
            routeId: props?.routeId,
        });
    }, [onStopClick]);

    const mapOptions: IAzureMapOptions = {
        ...(map ?? {}),
        center: (map?.center as [number, number] | undefined) ?? defaultCenter,
        zoom: map?.zoom ?? defaultZoom,
        authOptions: map?.authOptions ?? {
            authType: AuthenticationType.subscriptionKey,
            subscriptionKey: config.AzureMapKey,
        },
        style: map?.style ?? 'road',
        showLogo: map?.showLogo ?? false,
        showFeedbackLink: map?.showFeedbackLink ?? false,
        showTileBoundaries: map?.showTileBoundaries ?? false,
    };

    const mapControls: IAzureMapControls[] | undefined = React.useMemo(() => {
        if (!showZoomControls) {
            return undefined;
        }

        return [{
            controlName: 'ZoomControl',
            options: {
                position: ControlPosition.BottomRight,
            },
        }];
    }, [showZoomControls]);

    return (
        <div style={{
            width: '100%',
            height: '100%',
        }}>
            <AzureMapsProvider>
                <AzureMap options={mapOptions} controls={mapControls}>
                    <AzureMapCameraControl bounds={autoBounds} padding={DEFAULT_CAMERA_PADDING} />
                    <AzureMapSpriteRegistrar/>
                    <AzureMapDataSourceProvider id="lineDataSource">
                        <AzureMapLayerProvider
                            id="lineLayer"
                            type="LineLayer"
                            options={{
                                strokeColor: ['coalesce', ['get', 'strokeColor'], '#026db5'] as any,
                                strokeWidth: ['coalesce', ['get', 'strokeWidth'], 3] as any,
                            }}
                        />
                    </AzureMapDataSourceProvider>
                    <AzureMapDataSourceProvider id="line2DataSource" collection={lineFeatures}>
                        <AzureMapLayerProvider
                            id="line2Layer"
                            type="LineLayer"
                            options={{
                                strokeColor: ['coalesce', ['get', 'strokeColor'], '#026db5'] as any,
                                strokeWidth: ['coalesce', ['get', 'strokeWidth'], 3] as any,
                            }}
                            events={showRouteSegmentPopup
? {
                                mousemove: handleLineHover,
                                mouseout: handleLineLeave,
                            }
: undefined}
                        />
                    </AzureMapDataSourceProvider>
                    <AzureMapDataSourceProvider id="markerDataSource" collection={convertedFeatures} options={{ cluster: false }}>
                        <AzureMapLayerProvider
                            id="markerLayer"
                            type="SymbolLayer"
                            options={{
                                iconOptions: {
                                    ignorePlacement: true,
                                    iconAllowOverlap: true,
                                    image: [
                                        'case',
                                        ['==', ['get', 'icon'], 'stopSelectedIcon'], 'stopSelectedIcon',
                                        ['==', ['get', 'icon'], 'stopIcon'], 'stopIcon',
                                        ['==', ['get', 'icon'], 'busRed'], 'busRed',
                                        ['==', ['get', 'icon'], 'busRedWhite'], 'busRedWhite',
                                        ['==', ['get', 'icon'], 'busBlue'], 'busBlue',
                                        ['==', ['get', 'icon'], 'bus-pushpin'], 'bus-pushpin',
                                        ['==', ['get', 'color'], 'green'], 'pin-round-blue',
                                        ['==', ['get', 'color'], 'red'], 'pin-round-red',
                                        'pin-round-blue',
                                    ],
                                    offset: SYMBOL_MARKER_OFFSET_EXPRESSION,
                                    anchor: 'center',
                                },
                                textOptions: {
                                    textField: ['get', 'title'],
                                    allowOverlap: true,
                                    ignorePlacement: true,
                                },
                            }}
                            events={{
                                click: (e: any) => {
                                    const shape = e?.shapes?.[0];
                                    triggerStopClick(shape);
                                },
                            }}
                        />
                    </AzureMapDataSourceProvider>
                    <AzureMapDataSourceProvider id="bubblePinsDataSource" collection={loadBubble}>
                        <AzureMapLayerProvider
                            id="bubblePinsLayer"
                            type="BubbleLayer"
                            options={{
                                // Allow per-feature radius. If a feature doesn't have `radius`, fall back to 7.
                                radius: ['coalesce', ['get', 'radius'], 7] as any,
                                color: ['get', 'color'],
                                strokeColor: '#ffffff',
                                strokeWidth: 1,
                                blur: 0,
                                opacity: 0.95,
                            }}
                            events={{
                                click: (e: any) => {
                                    const shape = e?.shapes?.[0];
                                    triggerStopClick(shape);
                                },
                            }}
                        />
                    </AzureMapDataSourceProvider>

                    <AzureMapDataSourceProvider id="boardBubblePinsDataSource" collection={boardingBubble}>
                        <AzureMapLayerProvider
                            id="boardBubblePinsLayer"
                            type="BubbleLayer"
                            options={{
                                // Render on top for Boardings (green)
                                radius: ['coalesce', ['get', 'radius'], 1] as any,
                                color: '#2ecc71',
                                strokeColor: '#ffffff',
                                strokeWidth: 1,
                                blur: 0,
                                opacity: 0.9,
                            }}
                            events={{
                                click: (e: any) => {
                                    const shape = e?.shapes?.[0];
                                    triggerStopClick(shape);
                                },
                            }}
                        />
                    </AzureMapDataSourceProvider>

                    <AzureMapDataSourceProvider id="overlayBubblePinsDataSource" collection={alightingsBubble}>
                        <AzureMapLayerProvider
                            id="overlayBubblePinsLayer"
                            type="BubbleLayer"
                            options={{
                                // Render on top for Alightings (orange)
                                radius: ['coalesce', ['get', 'radius'], 1] as any,
                                color: '#f39c12',
                                strokeColor: '#ffffff',
                                strokeWidth: 1,
                                blur: 0,
                                opacity: 0.9,
                            }}
                            events={{
                                click: (e: any) => {
                                    const shape = e?.shapes?.[0];
                                    triggerStopClick(shape);
                                },
                            }}
                        />
                    </AzureMapDataSourceProvider>


                    <AzureMapDataSourceProvider id="busDataSource" collection={vehicleFeatures}>
                        <AzureMapLayerProvider
                            id="busLayer"
                            type="SymbolLayer"
                            options={{
                                iconOptions: {
                                    // allowOverlap: true,
                                    ignorePlacement: true,
                                    iconAllowOverlap: true,
                                    image: [
                                        'case',
                                        ['==', ['get', 'icon'], 'stopSelectedIcon'], 'stopSelectedIcon',
                                        ['==', ['get', 'icon'], 'stopIcon'], 'stopIcon',
                                        ['==', ['get', 'icon'], 'busRed'], 'busRed',
                                        ['==', ['get', 'icon'], 'busRedWhite'], 'busRedWhite',
                                        ['==', ['get', 'icon'], 'busBlue'], 'busBlue',
                                        ['==', ['get', 'icon'], 'bus-pushpin'], 'bus-pushpin',
                                        ['==', ['get', 'color'], 'green'], 'pin-round-blue',
                                        ['==', ['get', 'color'], 'red'], 'pin-round-red',
                                        'pin-round-blue',
                                    ],
                                    offset: [0, 0],
                                    anchor: 'center',
                                },
                                textOptions: {
                                    textField: ['get', 'title'],
                                },
                            }}
                        />
                    </AzureMapDataSourceProvider>

                    <>{popups}</>
                    <>{showRouteSegmentPopup ? hoverPopup : null}</>
                    {children as any}
                </AzureMap>
            </AzureMapsProvider>
        </div>
    );
};

export default AzureMapComponent;
